
import { Union, Record } from "../../fable_modules/fable-library-js.5.3.0/Types.js";
import { union_type, list_type, record_type, tuple_type, option_type, int32_type, bool_type, decimal_type, string_type } from "../../fable_modules/fable-library-js.5.3.0/Reflection.js";
import { printf, toText } from "../../fable_modules/fable-library-js.5.3.0/String.js";
import { map } from "../../fable_modules/fable-library-js.5.3.0/List.js";
import { fromParts, compare } from "../../fable_modules/fable-library-js.5.3.0/Decimal.js";

export const maxPollAttempts = 12;

/**
 * One tranche row in the Provisional or Earned variant.
 */
export class TrancheRow extends Record {
    constructor(Label, Percentage, Amount, Satisfied, ProgressHint, Nudge) {
        super();
        this.Label = Label;
        this.Percentage = Percentage;
        this.Amount = Amount;
        this.Satisfied = Satisfied;
        this.ProgressHint = ProgressHint;
        this.Nudge = Nudge;
    }
}

export function TrancheRow_$reflection() {
    return record_type("Shared.FeeShareHelpers.TrancheRow", [], TrancheRow, () => [["Label", string_type], ["Percentage", decimal_type], ["Amount", decimal_type], ["Satisfied", bool_type], ["ProgressHint", option_type(int32_type)], ["Nudge", option_type(tuple_type(string_type, string_type))]]);
}

/**
 * Provisional (active-phase) card state: on-track rows, running total, header subtitle, fine print.
 */
export class ProvisionalState extends Record {
    constructor(Subtitle, Rows, OnTrackLabel, TotalAccrued, TotalPotential, FinePrint) {
        super();
        this.Subtitle = Subtitle;
        this.Rows = Rows;
        this.OnTrackLabel = OnTrackLabel;
        this.TotalAccrued = TotalAccrued;
        this.TotalPotential = TotalPotential;
        this.FinePrint = FinePrint;
    }
}

export function ProvisionalState_$reflection() {
    return record_type("Shared.FeeShareHelpers.ProvisionalState", [], ProvisionalState, () => [["Subtitle", string_type], ["Rows", list_type(TrancheRow_$reflection())], ["OnTrackLabel", string_type], ["TotalAccrued", decimal_type], ["TotalPotential", decimal_type], ["FinePrint", string_type]]);
}

/**
 * Earned (finalized-and-processed) card state: earned/missed rows, accrued total, trophy flag.
 */
export class EarnedState extends Record {
    constructor(TotalLabel, TotalSubtitle, Rows, TotalAccrued, HasTrophy, FinePrint) {
        super();
        this.TotalLabel = TotalLabel;
        this.TotalSubtitle = TotalSubtitle;
        this.Rows = Rows;
        this.TotalAccrued = TotalAccrued;
        this.HasTrophy = HasTrophy;
        this.FinePrint = FinePrint;
    }
}

export function EarnedState_$reflection() {
    return record_type("Shared.FeeShareHelpers.EarnedState", [], EarnedState, () => [["TotalLabel", string_type], ["TotalSubtitle", string_type], ["Rows", list_type(TrancheRow_$reflection())], ["TotalAccrued", decimal_type], ["HasTrophy", bool_type], ["FinePrint", string_type]]);
}

export class Variant extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Provisional", "Calculating", "Earned"];
    }
}

export function Variant_$reflection() {
    return union_type("Shared.FeeShareHelpers.Variant", [], Variant, () => [[["Item", ProvisionalState_$reflection()]], [["timedOut", bool_type]], [["Item", EarnedState_$reflection()]]]);
}

function money(amount) {
    return toText(printf("$%.2f"))(amount);
}

function provisionalLabel(condition) {
    switch (condition) {
        case "updatePosted":
            return "Post at least one update";
        case "pledgeReceived":
            return "Receive a supporting pledge";
        case "goalReached":
            return "Hit your goal";
        default:
            return condition;
    }
}

function earnedLabel(condition) {
    switch (condition) {
        case "updatePosted":
            return "Posted an update";
        case "pledgeReceived":
            return "Received a pledge";
        case "goalReached":
            return "Hit your goal";
        default:
            return condition;
    }
}

function provisionalNudge(condition, satisfied, challengeId) {
    if (satisfied) {
        return undefined;
    }
    else {
        switch (condition) {
            case "updatePosted":
                return ["Post an update", "#daily-log"];
            case "pledgeReceived":
                return ["Share your challenge", toText(printf("/challenges/%O"))(challengeId)];
            default:
                return undefined;
        }
    }
}

function provisionalRow(challengeId, completionPct, t) {
    return new TrancheRow(provisionalLabel(t.Condition), t.Percentage, t.Amount, t.Satisfied, (t.Condition === "goalReached") ? completionPct : undefined, provisionalNudge(t.Condition, t.Satisfied, challengeId));
}

function earnedRow(t) {
    return new TrancheRow(earnedLabel(t.Condition), t.Percentage, t.Amount, t.Satisfied, undefined, undefined);
}

function provisionalFinePrint(challengeStatus) {
    if (challengeStatus === "finalizing") {
        return "Final results after your challenge is finalized.";
    }
    else {
        return "Bonus tranches are locked in when your challenge wraps up.";
    }
}

export function derive(response, challengeStatus, challengeId, charityName, completionPct, pollAttempts) {
    let arg, arg_3, arg_5, arg_6;
    const matchValue = response.Phase;
    switch (matchValue) {
        case "calculating":
            return new Variant(1, [pollAttempts >= maxPollAttempts]);
        case "earned": {
            const rows = map(earnedRow, response.Tranches);
            return new Variant(2, [new EarnedState((arg = money(response.TotalAccrued), toText(printf("%s for %s"))(arg)(charityName)), "earned from your challenge fee", rows, response.TotalAccrued, compare(response.TotalAccrued, fromParts(0, 0, 0, false, 0)) > 0, (compare(response.TotalAccrued, fromParts(0, 0, 0, false, 0)) > 0) ? toText(printf("Added to %s\'s monthly payout."))(charityName) : "No bonus tranches were earned this time.")]);
        }
        default: {
            const rows_1 = map((t_1) => provisionalRow(challengeId, completionPct, t_1), response.Tranches);
            return new Variant(0, [new ProvisionalState((arg_3 = money(response.TotalPotential), toText(printf("Up to %s of your fee can go to %s"))(arg_3)(charityName)), rows_1, (arg_5 = money(response.TotalAccrued), (arg_6 = money(response.TotalPotential), toText(printf("On track: %s of %s"))(arg_5)(arg_6))), response.TotalAccrued, response.TotalPotential, provisionalFinePrint(challengeStatus))]);
        }
    }
}

//# sourceMappingURL=FeeShareHelpers.js.map
